import { api } from '@api/api';
import { PaginatedResult } from '@api/types/paginated-result';
import { CompanyLocationRowResponse } from '@api/types/company/company-location/company-location-row.response';
import { PaginatedFilters } from '@api/types/paginated-filters';
import { makeError } from '@api/types/api-error';
import { CreateCompanyLocationRequest } from '@api/types/company/company-location/create-company-location.request';
import { CompanyLocationResource } from '@api/types/company/company-location/company-location.resource';

const companyLocationApi = api.injectEndpoints({
  endpoints: (build) => ({
    getLocationsForCompany: build.query<
      PaginatedResult<CompanyLocationRowResponse>,
      PaginatedFilters
    >({
      query: (params) => ({
        url: 'company/companyLocation',
        method: 'GET',
        params,
      }),
      providesTags: (response) =>
        response
          ? [
              ...response!.model.map((data) => ({
                id: data.companyLocationId,
                type: 'CompanyLocation' as const,
              })),
              'CompanyLocation',
            ]
          : ['CompanyLocation'],
    }),
    createLocationForCompany: build.mutation<
      CompanyLocationResource,
      CreateCompanyLocationRequest
    >({
      query: (body) => ({
        url: 'company/companyLocation',
        method: 'POST',
        body,
      }),
      transformErrorResponse: (error) =>
        makeError<CreateCompanyLocationRequest>(error),
      invalidatesTags: ['CompanyLocation'],
    }),
    getLocationForCompany: build.query<CompanyLocationResource, number>({
      query: (id) => ({
        url: `company/companyLocation/${id}`,
        method: 'GET',
      }),
      providesTags: (result) =>
        result
          ? [
              {
                id: result.companyLocationId,
                type: 'CompanyLocation' as const,
              },
              'CompanyLocation',
            ]
          : ['CompanyLocation'],
    }),
    deleteLocationForCompany: build.mutation<void, number>({
      query: (companyId) => ({
        url: `company/companyLocation/${companyId}`,
        method: 'DELETE',
      }),
      transformErrorResponse: (error) => makeError<never>(error),
      invalidatesTags: ['CompanyLocation'],
    }),
  }),
});

export const {
  useGetLocationsForCompanyQuery,
  useCreateLocationForCompanyMutation,
  useGetLocationForCompanyQuery,
  useDeleteLocationForCompanyMutation,
} = companyLocationApi;
